import React, { JSX, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  MenuItem,
  Checkbox,
  Typography,
  Grid,
  FormControlLabel,
  Alert,
  Button
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import upperFirst from "lodash/upperFirst";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { round } from "mathjs";
import { tl, t } from "../../components/translate";
import { getMedicineSearch } from "../../api/prescrption";
import { medicineType } from "../Assessment/Medication/medicineType";
import { ProductSubTypes, ServiceDeliveryModes, unitOfProducts } from "../../models/Service";
import ProductExtraInfo from "./ProductExtraInfo";
import hasOwnProperty from "../../helpers/object";
import { StateProductinfo } from "./ProductCreate";
import { LabTest } from "../../interfaces/Lab";
import AccountLedgerAutocomplete from "../../components/AccountLedgerAutocomplete/AccountLedgerAutocomplete";
import { ChildGeneralLedger, KnownLedgers, PrimaryLedgerNames } from "../../interfaces/Accounts";
import { RootState } from "../../store";
import VatOptionSelection from "../Stock/StockCreateEdit/VatOptionSelection";
import { getTaxationOptions } from "../Stock/StockCreateEdit/CreateStockProduct";
import useTaxations from "../../hooks/useTaxations";
import { defaultServiceTypes, specialLabCategories } from "./ServiceList";
import Modal from "../../components/Modal/Modal";
import LedgerSelect from "../ResourceCentre/Settings/AccountSettings/LedgerSelect";
import { findByCode } from "../accounts/hooks";
import { taxationPatch } from "../../slices/journalEntry";
import { TaxationInterface } from "../../interfaces/ProductInterface";
import { servicesSearchByCode } from "../../api/services";
import { ServiceInterface } from "../../interfaces/ServiceInterface";

interface ResourceCentreInfo {
  rcIds: Array<number>;
  name: string;
}

interface CommonServiceFieldsState {
  fieldData: StateProductinfo & {
    packageName: "string";
    packagePrice: number;
    departmentId: number;
    refTotalIncVAT?: number;
  };
  onUpdate: (updates: Record<string, any>) => void;
  labTests: LabTest[];
  forPublicBooking?: boolean;
  isPublicTab?: boolean;
  isOpd?: boolean;
  productId?: number;
  resourceCentres: ResourceCentreInfo[];
  isForEditingServiceUnderPackage?: boolean;
  packageClinicPriceError?: string;
  setPackageClinicPriceError?: React.Dispatch<React.SetStateAction<string>>;
  isSsfService?: boolean;
}

export const MedicationListView = (props: {
  brand: string;
  genericName: string;
  strength: string;
  form: string;
}): JSX.Element => {
  const { form, brand, strength, genericName } = props;
  return (
    <Box alignItems="center" display="flex" width="100%" pt="1rem" pb="1rem" pl="1rem">
      <Box>{medicineType[form] && medicineType[form]()}</Box>
      <Typography component="span" variant="body2">
        <Box pl={1} display="inline">
          {brand}
        </Box>
        <Box pl={0.5} display="inline">
          {strength}
        </Box>
        <Box pl={1}>{genericName}</Box>
      </Typography>
    </Box>
  );
};

export interface UpdateProps {
  info: { [key: string]: string };
  productisableId: number;
  productisableName: string;
}

const CommonServiceFields: React.FC<CommonServiceFieldsState> = ({
  fieldData,
  onUpdate,
  labTests = [],
  productId,
  forPublicBooking = false,
  isPublicTab = false,
  isOpd = false,
  resourceCentres = [],
  isForEditingServiceUnderPackage = false,
  packageClinicPriceError,
  setPackageClinicPriceError,
  isSsfService = false
}) => {
  const dispatch = useDispatch();
  const taxations = useTaxations() || [];

  const { salesTaxationOptions } = getTaxationOptions(taxations);
  const [duplicateServices, setDuplicateServices] = useState<ServiceInterface[]>([]);
  const [medicines, setMedicines] = React.useState([]);
  const [showTaxationMapAlert, setTaxationMapAlert] = React.useState<boolean>(false);
  const [selectedLedger, setSelectedLedger] = React.useState<TaxationInterface | null>(null);
  const [openTaxationMappingModal, setOpenTaxationMappingModal] = React.useState<boolean>(false);
  const [selectedResourceCentre, setSelectedResourceCentre] = React.useState({
    id: null,
    label: ""
  });

  const isAccountSubscribed = useSelector(
    (state: RootState) => state.subscriptions.currentSubscription?.features?.account?.subscribed
  );
  const rcServiceTypes = useSelector(
    (state: RootState) =>
      state.resources.resourceCentres[0]?.settings?.serviceSettings?.serviceTypes || []
  );
  const serviceTypes = forPublicBooking ? defaultServiceTypes : rcServiceTypes;

  const keyValues = Object.keys(fieldData);
  const priceKey: string = keyValues.includes("productPrice") ? "productPrice" : "packagePrice";

  const vatHetOnSalesLedger = [findByCode(KnownLedgers.VAT_ON_SALES)].filter(
    (x): x is ChildGeneralLedger => x !== null
  );

  const handleNonNumericInput = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const debouncedFetch = React.useMemo(
    () =>
      debounce((q) => {
        servicesSearchByCode(q, productId).then((res) => {
          if (res.length > 0) {
            setDuplicateServices(res);
          }
        });
      }, 700),
    []
  );

  React.useEffect(() => {
    const resourceCentre = resourceCentres.find((rc) => rc.rcIds.includes(fieldData.assignedRc));
    if (resourceCentre) {
      setSelectedResourceCentre({
        id: resourceCentre.rcIds,
        label: resourceCentre.name
      });
    }
  }, [fieldData.assignedRc, resourceCentres]);

  React.useEffect(() => {
    if (isAccountSubscribed && fieldData.salesTaxationId) {
      const selectedTaxationRule = salesTaxationOptions.find(
        (rule) => rule.id === fieldData.salesTaxationId
      );
      if (!selectedTaxationRule?.ledgerId) {
        setTaxationMapAlert(true);
      } else {
        setTaxationMapAlert(false);
      }
    }
  }, [fieldData.salesTaxationId, isAccountSubscribed, salesTaxationOptions]);

  return (
    <Box style={{ width: "100%" }}>
      {hasOwnProperty(fieldData, "productType") && (
        <Box gap={2} display="flex">
          <TextField
            label={tl("services")}
            select
            fullWidth
            disabled={isPublicTab || isOpd || isForEditingServiceUnderPackage || isSsfService}
            margin="dense"
            variant="outlined"
            data-testmation="productType"
            value={fieldData.productType}
            onChange={(e) =>
              onUpdate({
                productType: e.target.value,
                productisableId: null,
                productisableName: null
              })
            }
          >
            {serviceTypes.map((option) => (
              <MenuItem key={option} value={option} data-testmation="productMenuItem">
                {upperFirst(option)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {!["labTest", "medication", "medicalTest"].includes(fieldData.productType) ? (
        <>
          {hasOwnProperty(fieldData, "packageName") && (
            <TextField
              label={t("packages.name")}
              placeholder={t("services.enterPackageName")}
              fullWidth
              required
              disabled={isPublicTab}
              data-testmation={keyValues[0]}
              margin="dense"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              variant="outlined"
              value={fieldData.packageName}
              onChange={(e) => onUpdate({ packageName: e.target.value })}
            />
          )}
          {hasOwnProperty(fieldData, "productName") && (
            <TextField
              label={tl("services.name")}
              placeholder={t("services.enterServiceName")}
              fullWidth
              required
              data-testmation={keyValues[0]}
              disabled={isPublicTab || isForEditingServiceUnderPackage}
              margin="dense"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              variant="outlined"
              value={fieldData.productName}
              onChange={(e) => onUpdate({ productName: e.target.value })}
            />
          )}
        </>
      ) : (
        <Autocomplete
          freeSolo
          disableClearable
          disabled={isPublicTab || isForEditingServiceUnderPackage}
          inputValue={
            keyValues[0] === "packageName" ? fieldData.packageName : fieldData.productName
          }
          options={(fieldData.productType === "medication" ? medicines : labTests).map((item) => ({
            ...item,
            label: item.name || item.brand
          }))}
          getOptionLabel={(option) => {
            // Value selected with enter, right from the input
            if (typeof option === "string") {
              return option;
            }
            return fieldData.productType === "medication" ? option.brand : option.name;
          }}
          renderOption={(renderProps, option) => {
            if (option && fieldData.productType === "medication") {
              return (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li {...renderProps} key={option.id}>
                  <MedicationListView
                    form={option.form}
                    brand={option.brand}
                    strength={option.strength}
                    genericName={option.genericName}
                  />
                </li>
              );
            }
            return (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...renderProps} key={option.id}>
                {option.name}
              </li>
            );
          }}
          data-testmation={keyValues[0] === "packageName" ? "packageName" : "productName"}
          onChange={(e, v) => {
            let fieldName = v || "";
            const updates: UpdateProps = {} as UpdateProps;
            if (v && typeof v === "object") {
              if (fieldData.productType === "medication") {
                const { brand, genericName, strength, code, genericCode, form } = v;
                fieldName = v.strength
                  ? `${brand} (${String(form).substr(0, 3)}) (${strength})`
                  : `${brand} (${String(form).substr(0, 3)})`;
                updates.info = { brand, genericName, strength, code, genericCode, form };
              } else {
                updates.productisableId = v.id;
                updates.productisableName = "labTestGroup";
                fieldName = v.name;
              }
            }
            onUpdate({
              [keyValues[0] === "packageName" ? "packageName" : "productName"]: fieldName,
              ...updates
            });
          }}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              fullWidth
              disabled={isPublicTab || isForEditingServiceUnderPackage}
              label={tl("services.name")}
              placeholder={t("services.name")}
              slotProps={{
                inputLabel: { shrink: true }
              }}
              margin="dense"
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.length > 2) {
                  if (fieldData.productType === "medication") {
                    getMedicineSearch(e.target.value).then((res) => setMedicines(res));
                  }
                }
                onUpdate({
                  [keyValues[0] === "packageName" ? "packageName" : "productName"]: e.target.value
                });
              }}
            />
          )}
        />
      )}

      {["labTest", "medicalTest"].includes(fieldData.productType) && fieldData.productisableId && (
        <Typography style={{ fontSize: "0.75rem" }}>
          Linked to {labTests.find((test) => test.id === fieldData.productisableId)?.name}
        </Typography>
      )}

      {fieldData.info && (
        <ProductExtraInfo
          info={fieldData.info}
          clearInfo={isPublicTab ? null : () => onUpdate({ info: null })}
        />
      )}

      {(forPublicBooking || isPublicTab) &&
        ["labTest", ...specialLabCategories].includes(fieldData.productType) && (
          <TextField
            label="Special Lab Category"
            slotProps={{
              inputLabel: { shrink: true }
            }}
            select
            sx={{ width: "333px" }}
            disabled={isPublicTab}
            margin="dense"
            variant="outlined"
            data-testmation="specialLabTest"
            value={fieldData.specialLabTest}
            onChange={(e) =>
              onUpdate({
                specialLabTest: e.target.value
              })
            }
          >
            {specialLabCategories.map((option) => (
              <MenuItem key={option} value={option} data-testmation="specialLabTestCategory">
                {upperFirst(option)}
              </MenuItem>
            ))}
          </TextField>
        )}

      {(isPublicTab || forPublicBooking) && (
        <Box>
          <TextField
            label="Public Description"
            placeholder="Enter Public Description"
            multiline
            rows={4}
            disabled={isPublicTab}
            fullWidth
            data-testmation="publicDescription"
            margin="dense"
            slotProps={{
              inputLabel: { shrink: true }
            }}
            variant="outlined"
            value={fieldData.publicDescription}
            onChange={(e) => onUpdate({ publicDescription: e.target.value })}
          />
          <TextField
            label="Instructions"
            placeholder="Enter Instructions For Service"
            multiline
            rows={4}
            disabled={isPublicTab}
            fullWidth
            data-testmation="instructions"
            margin="dense"
            slotProps={{
              inputLabel: { shrink: true }
            }}
            variant="outlined"
            value={fieldData.instructions}
            onChange={(e) => onUpdate({ instructions: e.target.value })}
          />
        </Box>
      )}

      {forPublicBooking && (
        <>
          <FormControlLabel
            control={<Checkbox checked={fieldData.handledByOkhati} />}
            label="Handled By Okhati (Home Sample Collection)"
            onChange={() =>
              onUpdate({
                handledByOkhati: !fieldData.handledByOkhati
              })
            }
          />

          {fieldData.handledByOkhati && (
            <Autocomplete
              fullWidth
              freeSolo
              options={resourceCentres.map((rc) => ({ id: rc.rcIds, label: rc.name }))}
              value={selectedResourceCentre}
              data-testmation="assignedRc"
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label="Assign To"
                  placeholder="Assign To"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                  margin="dense"
                  variant="outlined"
                />
              )}
              onChange={(e, v) => {
                onUpdate({
                  // since rcIds always have single value inside array for resource centres
                  assignedRc: v?.id[0]
                });
              }}
            />
          )}
        </>
      )}

      <Grid container spacing={{ xs: 1.5 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label={tl("services.clinicPrice")}
            margin="dense"
            type="number"
            placeholder="0"
            required
            data-testmation={priceKey}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
              },
              inputLabel: { shrink: true }
            }}
            disabled={isSsfService}
            onFocus={(e) => e.target.select()}
            variant="outlined"
            error={fieldData[priceKey] === ""}
            value={fieldData[priceKey]}
            onKeyDown={(e) => handleNonNumericInput(e)}
            onChange={(e) => {
              const value = Number(e.target.value);
              onUpdate({ [priceKey]: value });
              // if vat used compare with refTotalIncVAT
              // if health tax is used, compare with refTotalIncVAT
              setPackageClinicPriceError?.(
                value > (fieldData?.refTotalIncVAT ?? 0)
                  ? "Price can't be greater than sum of clinic prices of services within package"
                  : ""
              );
            }}
            fullWidth
          />
          {packageClinicPriceError && (
            <Typography variant="body2" color="#E2A336">
              {packageClinicPriceError}
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label={tl("services.clinicPriceExcTax")}
            margin="dense"
            type="number"
            placeholder="0"
            required
            data-testmation={priceKey}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
              },
              inputLabel: { shrink: true }
            }}
            disabled={isSsfService}
            onFocus={(e) => e.target.select()}
            variant="outlined"
            error={fieldData[priceKey] === ""}
            value={fieldData.servicePriceExVAT}
            onKeyDown={(e) => handleNonNumericInput(e)}
            onChange={(e) => {
              const value = Number(e.target.value);

              let newPrice = 0;

              if (fieldData.vatPct > 0) {
                newPrice = round(value * (1 + fieldData.vatPct / 100), 2);
              }

              if (!fieldData.vatPct) {
                newPrice = value;
              }
              onUpdate({ [priceKey]: newPrice });
              setPackageClinicPriceError?.(
                newPrice > (fieldData?.refTotalIncVAT ?? 0)
                  ? "Price can't be greater than sum of clinic prices of services within package"
                  : ""
              );
            }}
            fullWidth
          />
          {packageClinicPriceError && (
            <Typography variant="body2" color="#E2A336">
              {packageClinicPriceError}
            </Typography>
          )}
        </Grid>

        {(isPublicTab || forPublicBooking) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Offer Price"
              placeholder="Enter Offer Price"
              fullWidth
              data-testmation="offerPrice"
              type="number"
              margin="dense"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              variant="outlined"
              value={fieldData.offerPrice}
              onChange={(e) => onUpdate({ offerPrice: e.target.value })}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            fullWidth
            freeSolo
            options={ProductSubTypes}
            value={fieldData.subType}
            data-testmation="subType"
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label={tl("service.subType")}
                placeholder={t("service.subType")}
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                margin="dense"
                variant="outlined"
              />
            )}
            onInputChange={(e, v) => {
              onUpdate({ subType: v });
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Service Code"
            placeholder="Service code (atleast 3 characters)"
            data-testmation="serviceCode"
            slotProps={{
              inputLabel: { shrink: true }
            }}
            disabled={isSsfService}
            onFocus={(e) => e.target.select()}
            variant="outlined"
            helperText={
              fieldData.serviceCode?.length && fieldData.serviceCode?.length < 3
                ? "At least 3 characters required"
                : false
            }
            value={fieldData.serviceCode}
            error={fieldData.serviceCode?.length ? fieldData.serviceCode?.length < 3 : false}
            onChange={(event) => {
              const { value } = event.target;
              onUpdate({ serviceCode: value });
              if (duplicateServices.length) {
                setDuplicateServices([]);
              }
              if (value.length >= 3) {
                debouncedFetch(value);
              }
            }}
            fullWidth
          />
        </Grid>

        {duplicateServices.length > 0 && (
          <Grid size={12}>
            <Alert variant="outlined" severity="info" sx={{ py: 0 }}>
              {`This service code is already used on service "${duplicateServices[0].name}"`}
            </Alert>
          </Grid>
        )}

        <Grid size={6}>
          <Autocomplete
            fullWidth
            freeSolo
            options={ServiceDeliveryModes}
            value={fieldData.serviceDeliveryMode}
            data-testmation="serviceDeliveryMode"
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label={tl("service.serviceDeliveryMode")}
                placeholder={t("service.serviceDeliveryMode")}
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                margin="dense"
                variant="outlined"
              />
            )}
            onInputChange={(e, v) => {
              onUpdate({ serviceDeliveryMode: v });
            }}
          />
        </Grid>

        <Grid size={6}>
          {hasOwnProperty(fieldData, "productUnits") && (
            <Autocomplete
              fullWidth
              freeSolo
              options={unitOfProducts.map((option) => option)}
              value={fieldData.productUnits}
              data-testmation="productUnits"
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label={tl("services.units")}
                  placeholder={t("services.units")}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                  margin="dense"
                  variant="outlined"
                />
              )}
              onInputChange={(e, v) => {
                onUpdate({ productUnits: v });
              }}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 1.5 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {isAccountSubscribed ? (
            <>
              <VatOptionSelection
                sx={{ width: "100%", margin: "0" }}
                value={salesTaxationOptions.find(
                  (option) => option.id === fieldData.salesTaxationId
                )}
                onChange={(v) => {
                  if (v) {
                    if (v.name === "VAT") {
                      onUpdate({
                        salesTaxationId: v?.id,
                        vatPct: Number(v.rate)
                      });
                    }
                  } else {
                    onUpdate({ salesTaxationId: null, vatPct: 0 });
                  }
                }}
                vatPercentageOptions={salesTaxationOptions}
                dataTestmation="vatOptionSelect"
                label="VAT Mapping"
                placeholder="Select VAT Mapping"
              />
              {showTaxationMapAlert && (
                <Alert
                  sx={{ position: "absolute", zIndex: 999 }}
                  action={
                    <Button
                      onClick={() => setOpenTaxationMappingModal(true)}
                      variant="outlined"
                      color="inherit"
                      size="small"
                    >
                      {tl("mapLedger")}
                    </Button>
                  }
                  severity="warning"
                >
                  {tl("TaxRuleMsg")}
                </Alert>
              )}
              <Modal
                open={openTaxationMappingModal}
                title={tl("SelectLedger")}
                footer={
                  <>
                    <Button onClick={() => setOpenTaxationMappingModal(false)}>Cancel</Button>
                    <Button
                      onClick={() => {
                        if (fieldData.salesTaxationId) {
                          dispatch(
                            taxationPatch({
                              id: fieldData.salesTaxationId,
                              ledgerId: selectedLedger?.id || null
                            })
                          );
                          setOpenTaxationMappingModal(false);
                        }
                      }}
                    >
                      Save
                    </Button>
                  </>
                }
              >
                <LedgerSelect
                  options={vatHetOnSalesLedger}
                  selected={selectedLedger}
                  onChange={(value) => setSelectedLedger(value)}
                />
              </Modal>
            </>
          ) : (
            <TextField
              type="number"
              label={tl("services.vatPct")}
              placeholder="0"
              margin="dense"
              variant="outlined"
              fullWidth
              data-testmation="vatPct"
              value={fieldData.vatPct}
              onChange={(e) => onUpdate({ vatPct: e.target.value, salesTaxationId: null })}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="start">
                      <Typography>%</Typography>
                    </InputAdornment>
                  )
                }
              }}
              onFocus={(e) => e.target.select()}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          {hasOwnProperty(fieldData, "materialCharge") && (
            <TextField
              type="number"
              label={tl("services.materialCharge")}
              placeholder="0"
              margin="dense"
              variant="outlined"
              fullWidth
              data-testmation="materialCharge"
              value={fieldData.materialCharge}
              onChange={(e) => onUpdate({ materialCharge: e.target.value || 0 })}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="start">
                      <Typography>%</Typography>
                    </InputAdornment>
                  )
                },
                inputLabel: { shrink: true }
              }}
              onFocus={(e) => e.target.select()}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          {hasOwnProperty(fieldData, "labCharge") && (
            <TextField
              type="number"
              label={tl("services.labCharge")}
              placeholder="0"
              margin="dense"
              fullWidth
              variant="outlined"
              data-testmation="labCharge"
              value={fieldData.labCharge}
              onChange={(e) => onUpdate({ labCharge: e.target.value || 0 })}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography>Rs</Typography>
                    </InputAdornment>
                  )
                }
              }}
              onFocus={(e) => e.target.select()}
            />
          )}
        </Grid>

        {isAccountSubscribed && (
          <Grid size={{ xs: 12 }}>
            <AccountLedgerAutocomplete
              ledgerId={fieldData.salesLedgerId}
              onChange={(v) => onUpdate({ salesLedgerId: v?.id })}
              ledgerType={PrimaryLedgerNames.INCOME}
            />
          </Grid>
        )}

        <Grid size={12}>
          {hasOwnProperty(fieldData, "isMultiSession") && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={fieldData.isMultiSession}
                  onChange={(e) => onUpdate({ isMultiSession: e.target.checked })}
                />
              }
              label="Is Multi Session Package"
            />
          )}
        </Grid>
      </Grid>

      <Box
        sx={{
          marginTop: 2,
          marginBottom: 3
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "16px",
            lineHeight: "24px",
            marginBottom: 2.5
          }}
        >
          {tl("services.clinicPriceTitle")}:
        </Typography>
        <Grid container>
          <Grid size={{ xs: 12, md: 8 }}>
            <Grid container>
              <Grid size={4}>
                <Typography style={{ fontSize: "14px", fontWeight: 500 }}>Without VAT</Typography>
              </Grid>

              <Grid size={4}>
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#5A607F"
                  }}
                >
                  Rs
                </Typography>
              </Grid>

              <Grid size={4}>
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#5A607F"
                  }}
                >
                  {fieldData.servicePriceExVAT}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {false && (
        <Box
          pb={3}
          display="flex"
          alignItems="center"
          style={{ borderBottom: "0.5px solid lightgray" }}
        >
          <Typography>Is Taxable</Typography>
          <Checkbox
            checked={fieldData.isTaxable}
            onChange={() => {
              const toggleVal = !fieldData.isTaxable;
              onUpdate({ isTaxable: toggleVal });
            }}
          />
        </Box>
      )}

      {isPublicTab && fieldData.handledByOkhati && (
        <Typography marginBottom={2}>
          Note: The sample will be collected from home by Okhati
        </Typography>
      )}
    </Box>
  );
};

export default CommonServiceFields;
